import { checklistActions } from "../constants";

/*
 * Application Data falls into three types: campaign-specific, user-specific, and "presentational".
 * For an overview of data types, read the documentation here:
 * https://confluence.build.ingka.ikea.com/display/USDHCXMF/Campaign+Checklist#CampaignChecklist-ApplicationData
 */
export const initialState = Object.freeze({
    title: "",
    subtitle: "",
    sublists: [],
    userCheckedItems: new Set(),
    userClosedSublists: new Set(),
    userToken: undefined,
    loaded: false,
    error: null, // for loading error, can't display checklist
    autosaveError: null,
    forcedLoggedOut: false
});

export const reducer = (state, action) => {
    switch (action.type) {
    case checklistActions.RESET_STATE:
        return {
            ...initialState,
            title: state.title,
            subtitle: state.subtitle,
            sublists: state.sublists,
            userToken: null,
            loaded: true
        };
    case checklistActions.SET_CAMPAIGN_DATA:
        return setCampaignData(state, action.payload);
    case checklistActions.SET_USER_CHECKED_ITEMS:
        return setUserCheckedItems(state, action.payload);
    case checklistActions.SET_USER_TOKEN:
        return setUserToken(state, action.payload);
    case checklistActions.TOGGLE_CHECK_ITEM:
        return toggleCheckItem(state, action.payload);
    case checklistActions.TOGGLE_SUBLIST_CLOSED_STATE:
        return toggleSublistClosedState(state, action.payload);
    case checklistActions.SET_CHECKLIST_ERROR:
        return setError(state, action.payload);
    case checklistActions.SET_AUTOSAVE_ERROR:
        return setAutosaveError(state, action.payload);
    case checklistActions.SET_LOADING:
        return setLoading(state, action.payload);
    case checklistActions.LOG_OUT:
        return setLogOutState(state);
    default:
        return state;
    }
};

/**
 * @param {Object} state - old state
 * @param {Object} payload - campaign-specific data pulled from Variera
 * @returns {Object} new state, including new campaign-specific data, and loading/error state
 */
const setCampaignData = (state, payload) => {
    return { ...state, ...payload };
};

const setError = (state, error) => {
    return { ...state, error: error };
};

const setAutosaveError = (state, error) => {
    return { ...state, autosaveError: error };
};

const setLoading = (state, loadingState) => {
    return { ...state, loaded: loadingState };
};

const setLogOutState = (state) => {
    return {
        ...initialState,
        title: state.title,
        subtitle: state.subtitle,
        sublists: state.sublists,
        loaded: true,
        userToken: null, 
        forcedLoggedOut: true
    };
};
/**
 * Called on page load.
 * @param {Object} state - old state
 * @param {Set} userCheckedItems - the unique ids the user has previously checked off
 * @returns {Object} new state, with updated userCheckedItemSet
 */
const setUserCheckedItems = (state, userCheckedItems) => {
    return { ...state, userCheckedItems: userCheckedItems, loaded: true };
};

/**
 * @param {Object} state - old state
 * @param {String} userToken - the signed in access token
 * @returns {Object} new state
 */
const setUserToken = (state, userToken) => {
    return { ...state, userToken: userToken, loaded: false, forcedLoggedOut: false };
};

/**
 * Called when user checks/unchecks an item.
 * @param {Object} state - old state
 * @param {Object} payload - an object containing the sublist id and item id for a sublist list item
 * @returns {Object} new state, with updated userCheckedItemSet
 */
const toggleCheckItem = (state, payload) => {
    const { sublistId, itemId } = payload;
    const key = `${sublistId}-${itemId}`;
    const clonedSet = new Set(state.userCheckedItems);

    if (clonedSet.has(key)) clonedSet.delete(key);
    else clonedSet.add(key);

    return { ...state, userCheckedItems: clonedSet };
};

/**
 * @param {Object} state - old state
 * @param {String} sublistId - id of the recently closed/opened sublist
 * @returns {Object} new state, with updated userClosedSublists set
 */
const toggleSublistClosedState = (state, sublistId) => {
    const clonedSet = new Set(state.userClosedSublists);

    if (clonedSet.has(sublistId)) clonedSet.delete(sublistId);
    else clonedSet.add(sublistId);

    return { ...state, userClosedSublists: clonedSet };
};
